import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import type { QuestionnaireQuestion, QuestionnaireSection } from '@/types/generated'

export interface SectionInfo {
  section: QuestionnaireSection
  sectionIndex: number
  startFieldIndex: number
  endFieldIndex: number
  isRepeatable: boolean
  participantType: string | null
}

export interface FieldSectionMapping {
  fieldUuid: string
  sectionUuid: string
  sectionInfo: SectionInfo
  isLastFieldInSection: boolean
}

/**
 * Isolated store for questionnaire flow preview.
 * Operates on a deep-cloned schema - no API calls, no persistence.
 */
export const useQuestionnairePreviewStore = defineStore('questionnairePreviewStore', () => {
  // Original schema (for reset)
  const originalSchema = ref<QuestionnaireSection[]>([])

  // Working state
  const formData = ref<Record<string, any>>({})
  const activeFieldId = ref('')
  const activeFieldIndex = ref(0)
  const flatFields = ref<QuestionnaireQuestion[]>([])
  const rawSchema = ref<QuestionnaireSection[]>([])
  const fieldSectionMap = ref<Map<string, FieldSectionMapping>>(new Map())

  // State for showing the repeat section prompt
  const showRepeatSectionPrompt = ref(false)
  const pendingRepeatSectionInfo = ref<SectionInfo | null>(null)

  // Preview completed state
  const isPreviewComplete = ref(false)

  // Computed properties
  const formLength = computed(() => flatFields.value.length)
  const isLastField = computed(() => activeFieldIndex.value >= flatFields.value.length)
  const isFirstField = computed(() => activeFieldIndex.value === 0)

  function updateFormData(fieldId: string, value: any) {
    formData.value[fieldId] = value
  }

  /**
   * Initialize the preview with a deep-cloned schema.
   * Call this when opening the preview dialog.
   */
  function initializePreview(schema: QuestionnaireSection[]) {
    // Deep clone to ensure complete isolation
    originalSchema.value = JSON.parse(JSON.stringify(schema))
    rawSchema.value = JSON.parse(JSON.stringify(schema))

    rebuildFlatFields()

    // Set initial active field to the first one
    activeFieldIndex.value = 0
    activeFieldId.value = flatFields.value[0]?.uuid || ''
    isPreviewComplete.value = false
  }

  /**
   * Rebuild flatFields and fieldSectionMap from rawSchema.
   * Called after initialization and after section duplication.
   */
  function rebuildFlatFields() {
    flatFields.value = []
    fieldSectionMap.value = new Map()

    let fieldIndex = 0

    if (Array.isArray(rawSchema.value)) {
      rawSchema.value.forEach((section, sectionIndex) => {
        const sectionStartIndex = fieldIndex
        const isRepeatable = (section as any).repeatable === true
        const participantType = (section as any).participant_type || null

        if (Array.isArray(section.items)) {
          const sectionFieldCount = section.items.length

          section.items.forEach((field, fieldIndexInSection) => {
            flatFields.value.push(field as QuestionnaireQuestion)

            const sectionInfo: SectionInfo = {
              section,
              sectionIndex,
              startFieldIndex: sectionStartIndex,
              endFieldIndex: sectionStartIndex + sectionFieldCount - 1,
              isRepeatable,
              participantType
            }

            const mapping: FieldSectionMapping = {
              fieldUuid: field.uuid,
              sectionUuid: section.uuid,
              sectionInfo,
              isLastFieldInSection: fieldIndexInSection === sectionFieldCount - 1
            }

            fieldSectionMap.value.set(field.uuid, mapping)
            fieldIndex++
          })
        }
      })
    }
  }

  function jumpToField(uuid: string) {
    setRepeatSectionPrompt(false)
    const index = flatFields.value.findIndex((field) => field.uuid === uuid)
    if (index !== -1) {
      activeFieldIndex.value = index
      activeFieldId.value = uuid
      isPreviewComplete.value = false
    }
  }

  function goToNextField() {
    const nextIndex = findNextField(activeFieldIndex.value)
    activeFieldIndex.value = nextIndex
    if (nextIndex >= flatFields.value.length) {
      isPreviewComplete.value = true
      activeFieldId.value = ''
    } else {
      activeFieldId.value = flatFields.value[nextIndex]?.uuid || ''
    }
  }

  function goToPreviousField() {
    setRepeatSectionPrompt(false)
    if (activeFieldIndex.value > 0) {
      activeFieldIndex.value--
      activeFieldId.value = flatFields.value[activeFieldIndex.value]?.uuid || ''
      isPreviewComplete.value = false
    }
  }

  function setRepeatSectionPrompt(show: boolean, sectionInfo: SectionInfo | null = null) {
    showRepeatSectionPrompt.value = show
    pendingRepeatSectionInfo.value = sectionInfo
  }

  /**
   * Find the next field index based on jump logic.
   * Replicates the logic from questionnaireStore.ts
   */
  function findNextField(currentIndex: number): number {
    const currentField = flatFields.value[currentIndex]

    if (currentField?.jump_to && currentField.jump_to.length > 0) {
      const jumpCondition = currentField.jump_to.find((jump) => {
        if (currentField.type === 'yesno') {
          const formDataValue = formData.value[currentField.uuid]
          const jumpValueLower = jump.value.toLowerCase()
          return (
            ((jumpValueLower === 'yes' || jumpValueLower === 'ja') && formDataValue === true) ||
            ((jumpValueLower === 'no' || jumpValueLower === 'nein') && formDataValue === false)
          )
        }
        return jump.value === formData.value[currentField.uuid]
      })
      if (jumpCondition) {
        if (jumpCondition.uuid === 'END') {
          return flatFields.value.length
        }
        const jumpIndex = flatFields.value.findIndex((field) => field.uuid === jumpCondition.uuid)
        if (jumpIndex !== -1) {
          return jumpIndex
        }
      }
    }
    return currentIndex + 1
  }

  function getFieldByUuid(uuid: string) {
    return flatFields.value.find((field) => field.uuid === uuid)
  }

  function getFieldSectionMapping(fieldUuid?: string): FieldSectionMapping | undefined {
    return fieldSectionMap.value.get(fieldUuid ?? activeFieldId.value)
  }

  // Count repetitions for a section
  function countRepetitionsForSection(section: QuestionnaireSection): number {
    const sectionAny = section as any
    const originalUuid = sectionAny.original_section_uuid || section.uuid

    return rawSchema.value.filter((s) => {
      const sAny = s as any
      return s.uuid === originalUuid || sAny.original_section_uuid === originalUuid
    }).length
  }

  // Check if we can add more repetitions
  function canAddMoreRepetitions(sectionInfo: SectionInfo): boolean {
    const maxRepetitions = (sectionInfo.section as any).max_repetitions
    if (maxRepetitions === null || maxRepetitions === undefined) {
      return true
    }
    const currentCount = countRepetitionsForSection(sectionInfo.section)
    return currentCount < maxRepetitions
  }

  // Check if the current field is the last field in a repeatable section
  const isLastFieldOfRepeatableSection = computed(() => {
    const mapping = fieldSectionMap.value.get(activeFieldId.value)
    if (!mapping) return false
    return mapping.isLastFieldInSection && mapping.sectionInfo.isRepeatable
  })

  // Get current section info for repeat prompt
  const currentRepeatableSectionInfo = computed(() => {
    const mapping = fieldSectionMap.value.get(activeFieldId.value)
    if (!mapping || !mapping.sectionInfo.isRepeatable) return null
    return mapping.sectionInfo
  })

  /**
   * Generate a unique section title for duplicated sections
   */
  function getUniqueSectionTitle(baseTitle: string): string {
    const existingTitles = rawSchema.value.map((s) => s.title)
    let newTitle = baseTitle
    let counter = 2

    while (existingTitles.includes(newTitle)) {
      newTitle = `${baseTitle} (${counter})`
      counter++
    }

    return newTitle
  }

  /**
   * Duplicate a section for preview (no API calls).
   * Called when user clicks "Yes, add another" on the repeat prompt.
   */
  function duplicateSectionForPreview() {
    if (!pendingRepeatSectionInfo.value) {
      handleContinueWithoutRepeat()
      return
    }

    const sectionInfo = pendingRepeatSectionInfo.value
    const originalSection = sectionInfo.section

    // Find the highest tag_number for this participant type
    let maxTagNumber = 0
    for (const section of rawSchema.value) {
      if ((section as any).participant_type === sectionInfo.participantType) {
        for (const item of section.items || []) {
          if ((item as any).tag_number && (item as any).tag_number > maxTagNumber) {
            maxTagNumber = (item as any).tag_number
          }
        }
      }
    }

    // Create a duplicate section with new UUIDs
    const originalSectionUuid =
      (originalSection as any).original_section_uuid || originalSection.uuid
    const newSection: QuestionnaireSection = {
      ...JSON.parse(JSON.stringify(originalSection)),
      uuid: uuidv4(),
      title: getUniqueSectionTitle(originalSection.title),
      original_section_uuid: originalSectionUuid
    } as QuestionnaireSection

    // Update items with new UUIDs and incremented tag_number
    let currentTagNumber = maxTagNumber
    newSection.items = (originalSection.items || []).map((item: QuestionnaireQuestion) => ({
      ...JSON.parse(JSON.stringify(item)),
      uuid: uuidv4(),
      tag_number: ++currentTagNumber,
      value: null
    }))

    // Find the index of the original section and insert the new one after it
    const originalSectionIndex = rawSchema.value.findIndex(
      (s: QuestionnaireSection) => s.uuid === originalSection.uuid
    )
    const insertIndex = originalSectionIndex + 1

    // Insert the new section
    rawSchema.value.splice(insertIndex, 0, newSection)

    // Rebuild flatFields with the new schema
    rebuildFlatFields()

    // Jump to the first question of the new section
    const firstQuestionUuid = newSection.items?.[0]?.uuid
    if (firstQuestionUuid) {
      jumpToField(firstQuestionUuid)
    }

    setRepeatSectionPrompt(false, null)
  }

  /**
   * Continue without repeating the section.
   */
  function handleContinueWithoutRepeat() {
    setRepeatSectionPrompt(false, null)
    goToNextField()
  }

  /**
   * Reset the preview to its initial state.
   * Clears all answers and restores original schema.
   */
  function resetPreview() {
    formData.value = {}
    rawSchema.value = JSON.parse(JSON.stringify(originalSchema.value))
    rebuildFlatFields()
    activeFieldIndex.value = 0
    activeFieldId.value = flatFields.value[0]?.uuid || ''
    showRepeatSectionPrompt.value = false
    pendingRepeatSectionInfo.value = null
    isPreviewComplete.value = false
  }

  /**
   * Get the current active field
   */
  const activeField = computed(() => {
    return flatFields.value[activeFieldIndex.value] || null
  })

  return {
    // State
    formData,
    activeFieldId,
    activeFieldIndex,
    flatFields,
    rawSchema,
    showRepeatSectionPrompt,
    pendingRepeatSectionInfo,
    isPreviewComplete,

    // Computed
    formLength,
    isLastField,
    isFirstField,
    isLastFieldOfRepeatableSection,
    currentRepeatableSectionInfo,
    activeField,

    // Actions
    updateFormData,
    initializePreview,
    jumpToField,
    goToNextField,
    goToPreviousField,
    setRepeatSectionPrompt,
    getFieldByUuid,
    getFieldSectionMapping,
    canAddMoreRepetitions,
    duplicateSectionForPreview,
    handleContinueWithoutRepeat,
    resetPreview
  }
})
